/**
 * Boot splash.
 *
 * Renders a 25×25 grid styled after a QR code into the `#app-splash`
 * element that index.html declares. Three corner finder patterns
 * anchor the layout, the word "EZQR" sits in the middle as a
 * 5-row × 4-col-per-letter dot font, and every cell that's neither
 * finder nor letter twinkles at a randomised phase so the whole
 * surface feels alive.
 *
 * Theme sync: before rendering, we read the same `ezqr:theme` storage
 * key `useTheme` reads later and set the `dark` class on `<html>`
 * accordingly. That way the splash colours match whatever the app
 * will mount with — no jarring light-to-dark (or vice versa) flash
 * at hand-off.
 *
 * Adaptive sizing: CSS uses `clamp()` on the cell + gap so the QR
 * fits any viewport from ~280px wide up to a max-cell ceiling, and
 * `size` lets callers reuse the layout at smaller scales later.
 *
 * Lifecycle:
 *  - `mountSplash()` runs at module init (called from main.ts BEFORE
 *    Pinia / Vue setup), so the splash is up the instant our JS
 *    bundle parses — well before the route view finishes loading.
 *  - `unmountSplash()` is called after `app.mount('#app')` resolves;
 *    it adds the `fade` class for a 300ms opacity transition and
 *    removes the element when that finishes.
 *
 * No external state, no DOM observation tricks — keep it cheap and
 * dependency-free so it can't get in its own way.
 */

const GRID = 25

/** Keep aligned with `src/shared/lib/useTheme.ts` — must read the same key. */
const THEME_STORAGE_KEY = 'ezqr:theme'

/**
 * Mirror useTheme's bootstrap on the document root so the splash
 * matches the app's final theme before it even paints.
 */
function applyTheme(): void {
  if (typeof document === 'undefined') return
  const osDark = !!window.matchMedia?.('(prefers-color-scheme: dark)').matches
  let dark = osDark
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    // 'system' (or anything else) → follow OS; 'light'/'dark' override.
    if (stored === 'dark') dark = true
    else if (stored === 'light') dark = false
  } catch {
    // localStorage can throw in private-mode Safari and similar — the
    // splash falls through to the prefers-color-scheme reading above.
  }
  document.documentElement.classList.toggle('dark', dark)
}

/** Keep aligned with `src/shared/lib/useAccent.ts` — same key + values. */
const ACCENT_STORAGE_KEY = 'ezqr:accent'

/**
 * Mirror useAccent's bootstrap so the first paint already carries the
 * chosen accent (no default-green flash before the stored purple).
 */
function applyAccent(): void {
  if (typeof document === 'undefined') return
  try {
    const stored = localStorage.getItem(ACCENT_STORAGE_KEY)
    // Only non-default accents need the attribute; `green` is the @theme default.
    if (stored && stored !== 'green') {
      document.documentElement.setAttribute('data-accent', stored)
    }
  } catch {
    // localStorage can throw in private-mode Safari and similar — ignore.
  }
}

/** Visual roles for each cell — drives the CSS class picked below. */
type CellKind = 'empty' | 'finder' | 'letter'

function blankGrid(): CellKind[][] {
  return Array.from({ length: GRID }, () => Array<CellKind>(GRID).fill('empty'))
}

/** Standard QR finder: 7×7 outer ring + 3×3 solid center. */
function placeFinder(g: CellKind[][], r0: number, c0: number): void {
  for (let r = 0; r < 7; r++) {
    for (let c = 0; c < 7; c++) {
      const edge = r === 0 || r === 6 || c === 0 || c === 6
      const inner = r >= 2 && r <= 4 && c >= 2 && c <= 4
      if (edge || inner) g[r0 + r]![c0 + c] = 'finder'
    }
  }
}

/**
 * 4×5 pixel font for the wordmark. Hand-tuned to read at small sizes;
 * not from any system font. Each row is 4 cells, 5 rows total.
 */
const LETTERS: Record<'E' | 'Z' | 'Q' | 'R', readonly (readonly number[])[]> = {
  E: [
    [1, 1, 1, 1],
    [1, 0, 0, 0],
    [1, 1, 1, 0],
    [1, 0, 0, 0],
    [1, 1, 1, 1],
  ],
  Z: [
    [1, 1, 1, 1],
    [0, 0, 0, 1],
    [0, 0, 1, 0],
    [0, 1, 0, 0],
    [1, 1, 1, 1],
  ],
  Q: [
    [0, 1, 1, 0],
    [1, 0, 0, 1],
    [1, 0, 0, 1],
    [1, 0, 1, 1],
    [0, 1, 1, 1],
  ],
  R: [
    [1, 1, 1, 0],
    [1, 0, 0, 1],
    [1, 1, 1, 0],
    [1, 0, 1, 0],
    [1, 0, 0, 1],
  ],
} as const

function placeLetter(
  g: CellKind[][],
  r0: number,
  c0: number,
  glyph: readonly (readonly number[])[],
): void {
  for (let r = 0; r < glyph.length; r++) {
    const row = glyph[r]!
    for (let c = 0; c < row.length; c++) {
      if (row[c]) g[r0 + r]![c0 + c] = 'letter'
    }
  }
}

function buildGrid(): CellKind[][] {
  const g = blankGrid()
  // Three of the four corners — the bottom-right is intentionally
  // empty so the wordmark has breathing room and a real QR's
  // alignment-pattern asymmetry is gestured at.
  placeFinder(g, 0, 0)
  placeFinder(g, 0, GRID - 7)
  placeFinder(g, GRID - 7, 0)

  // Letters centred horizontally on a 5-row band at the middle.
  // 4 letters × 4 cols + 3 single-col gaps = 19 cells wide → margin 3.
  const startCol = 3
  const stride = 5 // 4 cols + 1 gap
  const row = 10
  placeLetter(g, row, startCol + 0 * stride, LETTERS.E)
  placeLetter(g, row, startCol + 1 * stride, LETTERS.Z)
  placeLetter(g, row, startCol + 2 * stride, LETTERS.Q)
  placeLetter(g, row, startCol + 3 * stride, LETTERS.R)
  return g
}

/**
 * Build the splash markup once. Cheap enough to render synchronously:
 * 625 `<i>` elements, ~30KB of HTML, all server-side computable.
 */
function render(): string {
  const grid = buildGrid()
  const cells: string[] = []
  let twinkleIdx = 0
  for (let r = 0; r < GRID; r++) {
    for (let c = 0; c < GRID; c++) {
      const kind = grid[r]![c]!
      if (kind === 'finder') {
        cells.push('<i class="on finder"></i>')
      } else if (kind === 'letter') {
        cells.push('<i class="on letter"></i>')
      } else {
        // Spread twinkle phases across ~2.4s so the field shimmers
        // instead of pulsing in unison. Prime stride keeps neighbours
        // out of phase with each other.
        const delayDs = (twinkleIdx * 137) % 24
        twinkleIdx++
        cells.push(`<i class="twinkle" style="animation-delay:-${delayDs}00ms"></i>`)
      }
    }
  }
  return `<div class="qr" aria-hidden="true">${cells.join('')}</div>
    <p class="sr-only" role="status">Loading EZ-QR…</p>`
}

/**
 * Visual size of the QR. `splash` is the boot screen default — small
 * enough on desktop to read as a logo, not a billboard. The other
 * sizes are for in-app reuse later (e.g. an empty-state spinner).
 * The mapping lives in index.html (CSS variant per `data-size`).
 */
export type SplashSize = 'splash' | 'sm' | 'md' | 'lg'

/** Visual style of background (non-on) cells. Squares match real QRs. */
export type SplashShape = 'square' | 'circle'

export interface MountSplashOptions {
  size?: SplashSize
  shape?: SplashShape
}

export function mountSplash(opts: MountSplashOptions = {}): void {
  const root = document.getElementById('app-splash')
  if (!root || root.dataset.mounted === '1') return
  applyTheme()
  applyAccent()
  root.dataset.size = opts.size ?? 'splash'
  root.dataset.shape = opts.shape ?? 'square'
  root.innerHTML = render()
  root.dataset.mounted = '1'
}

export function unmountSplash(): void {
  const root = document.getElementById('app-splash')
  if (!root) return
  root.classList.add('fade')
  // Match the CSS transition duration (.fade -> 300ms) plus a small
  // margin so the element is gone before any focus-stealing happens
  // on the freshly mounted view.
  window.setTimeout(() => root.remove(), 320)
}

/**
 * Replace the boot splash with a minimal, self-contained fatal-error
 * screen. The app never mounted (bad env, bootstrap threw), so we can't
 * rely on Vue, the router, or even `main.css` being applied — styles
 * are inlined and the only action is a hard reload. Without this the
 * splash spins forever on a fatal boot failure.
 *
 * Colours flip on the `dark` class that `applyTheme()` already set on
 * the document root before mountSplash() ran — without this the error
 * rendered dark-text on the splash's dark-blue backdrop on dark theme,
 * which was both unreadable and obviously broken on top of the actual
 * "something failed" message.
 */
export function showSplashError(detail?: string): void {
  const root = document.getElementById('app-splash')
  if (!root) return
  root.classList.remove('fade')
  root.dataset.mounted = '1'
  const dark = document.documentElement.classList.contains('dark')
  // Two-tone palette that matches the splash backdrop CSS in index.html.
  // Foreground reads against the backdrop; the muted variants stay
  // legible without competing with the heading.
  const fg = dark ? '#f8fafc' : '#1f2937'
  const fgMuted = dark ? '#cbd5e1' : '#6b7280'
  const fgQuiet = dark ? '#94a3b8' : '#9ca3af'
  const btnBg = dark ? '#1e293b' : '#fff'
  const btnBorder = dark ? '#334155' : '#d1d5db'
  root.innerHTML = `
    <div role="alert" style="max-width:28rem;margin:auto;padding:1.5rem;text-align:center;font-family:system-ui,sans-serif;color:${fg};">
      <h1 style="font-size:1.125rem;font-weight:600;margin:0 0 .5rem;">Couldn’t start the editor</h1>
      <p style="font-size:.875rem;color:${fgMuted};margin:0 0 1rem;">
        Something went wrong while loading. Please try again.
      </p>
      ${detail ? `<pre style="font-size:.75rem;color:${fgQuiet};white-space:pre-wrap;word-break:break-word;margin:0 0 1rem;">${escapeHtml(detail)}</pre>` : ''}
      <button type="button" id="app-splash-reload"
        style="cursor:pointer;border:1px solid ${btnBorder};border-radius:.375rem;padding:.5rem 1rem;font-size:.875rem;background:${btnBg};color:${fg};">
        Reload
      </button>
    </div>`
  document
    .getElementById('app-splash-reload')
    ?.addEventListener('click', () => window.location.reload())
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      (
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }) as Record<
          string,
          string
        >
      )[c]!,
  )
}

mountSplash()
